import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the InterviewsContext
const MyInterviewsContext = createContext();

// Custom hook to use the InterviewsContext
export const useMyInterviews = () => useContext(MyInterviewsContext);

// Provider component
export const MyInterviewsProvider = ({ children }) => {
  const [interviews, setInterviews] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const API_URL = "https://hrmsnew.prepstripe.com/api/my-interviews"; // Replace with your actual API URL
  const API_URL2 = "https://hrmsnew.prepstripe.com/apiinterviews/feedback";
  const token = getToken();

  // Fetch interviews from the API
  // const fetchInterviews = async (page = 1) => {
  //   setLoading(true);
  //   try {
  //     const response = await axios.get(API_URL, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });
  //     // console.log("fetch interviewS:", response.data?.data?.data || [])
  //     setInterviews(response.data?.data?.data || []); // Assuming interviews are in `data` key
  //     setPagination({
  //       currentPage: response.current_page,
  //       totalPages: response.last_page,
  //       totalItems: response.total,
  //       perPage: response.per_page,
  //     });
  //     setError(null);
  //   } catch (error) {
  //     console.error("Error fetching interviews:", error);
  //     setError(error.response?.data?.message || "Failed to fetch interviews.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchMyInterviews = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // console.log("fetchMyInterviews:", response.data);

      setInterviews(response.data?.data?.data || []); // Assuming interviews are inside `data.data.data`
      setPagination({
        currentPage: response.data?.data?.current_page || 1,
        totalPages: response.data?.data?.last_page || 1,
        totalItems: response.data?.data?.total || 0,
        perPage: response.data?.data?.per_page || 10,
      });
      setError(null);
    } catch (error) {
      console.error("Error fetching my interviews:", error);
      setError(
        error.response?.data?.message || "Failed to fetch my interviews.",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateMyInterviewStatus = async (interviewId, feedback) => {
    // console.log("interviewid: ", interviewId, "status: ", feedback.status, "feedback: ", feedback.overallFeedback)
    try {
      const response = await axios.post(
        `${API_URL2}`,
        {
          id: interviewId,
          status: feedback.status,
          feedback: feedback.overallFeedback,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      // Update the local state after a successful update
      setInterviews((prevInterviews) =>
        prevInterviews.map((interview) =>
          interview.id === interviewId
            ? { ...interview, status: feedback.status }
            : interview,
        ),
      );

      return response.data;
    } catch (error) {
      console.error("Error updating interview status:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchMyInterviews();
  }, [API_URL, token]); // Re-fetch only if API_URL or token changes

  // Memoize the context value
  const value = useMemo(
    () => ({
      interviews,
      pagination,
      loading,
      error,
      fetchMyInterviews,
      updateMyInterviewStatus,
    }),
    [interviews, loading, error], // Recalculate the value only when these dependencies change
  );

  return (
    <MyInterviewsContext.Provider value={value}>
      {children}
    </MyInterviewsContext.Provider>
  );
};
